import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import * as lodash from 'lodash'
import React from 'react'
import { classToFComponent, FCClassContext, MapSlider, MapToolbarGap } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'

import { MapCardScope, SliderScope } from '../tcm_scopes'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import AddIcon from '@mui/icons-material/Add'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import CropIcon from '@mui/icons-material/Crop'
import DeleteSweep from '@mui/icons-material/DeleteSweep'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import RemoveIcon from '@mui/icons-material/Remove'
import RouteIcon from '@mui/icons-material/Route'
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica'
import TripOriginIcon from '@mui/icons-material/TripOrigin'

import * as L from 'leaflet'
import { MapContainer, TileLayer } from 'react-leaflet'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-VIEW-MAP-CARD')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        overflow: 'hidden'
    },
    map: {
        display: 'flex',
        flexGrow: 1
    },
    toolButton: {
        width: 34,
        height: 34,
        padding: 5,
        cursor: 'pointer',
        color: '#2d332f',
        backgroundColor: 'var(--surface)',
        borderLeft: '1px solid var(--border-strong)',
        borderRight: '1px solid var(--border-strong)',
        '&:hover': {
            backgroundColor: '#edf1ee'
        }
    },
    toolButtonActive: {
        color: 'var(--green-800)',
        backgroundColor: 'var(--green-050)'
    },
    topToolButton: {
        borderTop: '1px solid var(--border-strong)',
        borderTopLeftRadius: 9,
        borderTopRightRadius: 9
    },
    bottomToolButton: {
        borderBottom: '1px solid var(--border-strong)',
        borderBottomLeftRadius: 9,
        borderBottomRightRadius: 9
    },
    expandButton: {
        position: 'absolute',
        top: 10,
        right: 12,
        zIndex: 1001,
        width: 34,
        height: 34,
        borderRadius: 8,
        color: '#2d332f',
        backgroundColor: 'rgba(255, 255, 255, .92)',
        border: '1px solid var(--border-strong)',
        '&:hover': { backgroundColor: '#edf1ee' }
    }
})

const MapToolbar = styled('div')({
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 1100,
    boxShadow: 'var(--shadow-1)',
    backgroundClip: 'padding-box',
    background: 'transparent',
    // layout
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    borderRadius: 9
})

export type MapCardViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: MapCardScope
}

class MapCardViewClass implements FCClassContext<MapCardViewProps> {
    // :: State

    scope!: MapCardScope
    mapRef: L.Map | null = null
    mapResizeObserver = new ResizeObserver(this.onMapResize.bind(this))
    paperEl: HTMLDivElement | null = null

    // :: Emissors

    readonly emitOnPaperAttached = (paperEl: HTMLDivElement | null) => {
        this.paperEl && this.mapResizeObserver.unobserve(this.paperEl)
        this.paperEl = paperEl
        this.paperEl && this.mapResizeObserver.observe(this.paperEl)
    }

    readonly emitExpand = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunk = () => this.scope.onShrunkClick().catch(LOG.caught)
    readonly emitClearFilters = () => this.scope.onClearFilters().catch(LOG.caught)
    readonly emitZoomIn = () => (this.mapRef && this.mapRef.zoomIn(1), void 0)
    readonly emitZoomOut = () => (this.mapRef && this.mapRef.zoomOut(1), void 0)
    readonly emitSetRouteMode = () => this.scope.onSetRouteMode().catch(LOG.caught)
    readonly emitSetAreaMode = () => this.scope.onSetAreaMode().catch(LOG.caught)
    readonly emitSetCircleMode = () => this.scope.onSetCircleMode().catch(LOG.caught)
    readonly emitSetMapMode = () => this.scope.onSetMapMode().catch(LOG.caught)

    readonly emitMapChanged = (map: L.Map | null) => {
        if (this.mapRef !== map) {
            this.mapRef = map
            this.scope.onMapChanged(map).catch(LOG.caught)
        }
    }

    // :: Methods

    onDetach() {
        this.paperEl && this.mapResizeObserver.unobserve(this.paperEl)
        this.mapResizeObserver.disconnect()
    }

    onSyncState({ scope }: MapCardViewProps) {
        this.scope = scope
    }

    onMapResize() {
        this.mapRef && this.mapRef.invalidateSize()
    }

    render({ className, style, scope }: MapCardViewProps) {
        const classes = useStyles().classes

        const mode = (active: boolean) => clsx(classes.toolButton, active && classes.toolButtonActive)
        const topToolButton = clsx(classes.toolButton, classes.topToolButton)
        const bottomToolButton = clsx(classes.toolButton, classes.bottomToolButton)
        const singleToolButton = clsx(classes.toolButton, classes.topToolButton, classes.bottomToolButton)

        return (
            <TcmCard caption={scope.caption} className={className} style={style}>
                <div ref={this.emitOnPaperAttached} className={classes.view}>
                    <MapToolbar>
                        <Tooltip title="Aproximar">
                            <AddIcon className={clsx(topToolButton)} aria-label="Aproximar" onClick={this.emitZoomIn} />
                        </Tooltip>
                        <Tooltip title="Afastar">
                            <RemoveIcon
                                className={clsx(bottomToolButton)}
                                aria-label="Afastar"
                                onClick={this.emitZoomOut}
                            />
                        </Tooltip>
                        <MapToolbarGap />
                        <Tooltip title="Selecionar por rota">
                            <RouteIcon
                                className={clsx(mode(scope.mode === 'route'), classes.topToolButton)}
                                aria-label="Selecionar por rota"
                                onClick={this.emitSetRouteMode}
                            />
                        </Tooltip>
                        <Tooltip title="Selecionar por área">
                            <CropIcon
                                className={mode(scope.mode === 'polygon')}
                                aria-label="Selecionar por área"
                                onClick={this.emitSetAreaMode}
                            />
                        </Tooltip>
                        <Tooltip title="Selecionar por círculo">
                            <TripOriginIcon
                                className={mode(scope.mode === 'circle')}
                                aria-label="Selecionar por círculo"
                                onClick={this.emitSetCircleMode}
                            />
                        </Tooltip>
                        <Tooltip title="Selecionar por mapa">
                            <SouthAmericaIcon
                                className={clsx(mode(scope.mode === 'map'), classes.bottomToolButton)}
                                aria-label="Selecionar por mapa"
                                onClick={this.emitSetMapMode}
                            />
                        </Tooltip>
                        {scope.showClearButton && (
                            <>
                                <MapToolbarGap />
                                <Tooltip title="Limpar filtros aplicados ao mapa">
                                    <DeleteSweep
                                        className={singleToolButton}
                                        aria-label="Limpar filtros aplicados ao mapa"
                                        onClick={this.emitClearFilters}
                                    />
                                </Tooltip>
                            </>
                        )}
                    </MapToolbar>
                    <MapContainer
                        className={classes.map}
                        zoomControl={false}
                        scrollWheelZoom={true}
                        ref={this.emitMapChanged}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    </MapContainer>
                    {scope.slider.visible && <CustomSlider scope={scope.slider} />}
                    <IconButton
                        className={classes.expandButton}
                        size="small"
                        aria-label={scope.expanded ? 'Restaurar mapa' : 'Maximizar mapa'}
                        onClick={scope.expanded ? this.emitShrunk : this.emitExpand}
                    >
                        {scope.expanded && <CloseFullscreenRoundedIcon fontSize="small" />}
                        {!scope.expanded && <LaunchRoundedIcon fontSize="small" />}
                    </IconButton>
                </div>
            </TcmCard>
        )
    }
}
export const MapCardView = classToFComponent(MapCardViewClass, React)
export default MapCardView

// :: Internal

export type CustomSliderProps = {
    className?: string
    style?: React.CSSProperties
    scope: SliderScope
}

const CustomSlider = classToFComponent(
    class CustomSliderClass implements FCClassContext<CustomSliderProps> {
        scope!: SliderScope

        render({ className, style, scope }: CustomSliderProps): React.JSX.Element {
            this.scope = scope
            return (
                <MapSlider
                    className={className}
                    style={style}
                    size="small"
                    aria-label="Raio de seleção"
                    valueLabelDisplay="auto"
                    value={scope.value}
                    min={scope.min}
                    max={scope.max}
                    step={scope.step}
                    valueLabelFormat={this.formatSliderLabel}
                    onChange={this.emitKmSliderChange}
                />
            )
        }

        readonly emitKmSliderChange = (_event: Event, value: number | number[]) => {
            if (lodash.isNumber(value)) {
                this.scope.onValueChanged(value)
            }
        }

        readonly formatSliderLabel = (value: number) => {
            return `${value}${this.scope.unit}`
        }
    },
    React
)
